import './components/_cookies';

// accessibility: prefs first (exposes window.a11yPrefs), then the Alpine store
import './a11y-prefs';
import './a11y';

// alpine
import Alpine from 'alpinejs';
import intersect from '@alpinejs/intersect'
import focus from '@alpinejs/focus'
import collapse from '@alpinejs/collapse'
import registerLiveSearch from './components/live-search';
import registerTtsPlayer from './components/tts-player';
import registerExpandableCaption from './components/expandable-caption';
import registerWelcomePopup from './components/welcome-popup';
import registerFacetCombobox from './components/facet-combobox';
import initParallax from './components/parallax';
import initImageProtection from './components/image-protection';
import initRedirectHere from './components/redirect-here';

Alpine.plugin(focus)
Alpine.plugin(intersect)
Alpine.plugin(collapse)

registerLiveSearch(Alpine);
registerTtsPlayer(Alpine);
registerExpandableCaption(Alpine);
registerWelcomePopup(Alpine);
registerFacetCombobox(Alpine);

window.Alpine = Alpine;
Alpine.start();

initParallax();
initImageProtection();
initRedirectHere();

// Swiper, Fancybox and Leaflet together were the bulk of the bundle while each of them
// appears on only a handful of templates. They are now loaded from their own chunks, on
// the pages that actually contain the markup they drive.

let slidersRequested = false;

// Waits until a slider is almost in view, so on a phone Swiper does not share the
// first second of bandwidth with the banner photo.
function loadSliders() {
    const sliders = document.querySelectorAll('.swiper, .swiper-kentekens, .swiper-items, .swiper-photos');

    if (slidersRequested || !sliders.length) {
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
            return;
        }

        observer.disconnect();

        if (slidersRequested) {
            return;
        }

        slidersRequested = true;
        import('./components/swiper').then((module) => module.default());
    }, { rootMargin: '400px 0px' });

    sliders.forEach((slider) => observer.observe(slider));
}

let lightboxReady = null;

function bootLightbox() {
    lightboxReady ??= import('./components/fancybox').then((module) => module.default());

    return lightboxReady;
}

// Fancybox is 140 KB that most visitors never open, so it loads when a trigger is
// hovered or focused, and a click that beats the download is replayed once it is in.
function loadLightbox() {
    const triggerFrom = (event) => event.target.closest?.('[data-fancybox]');
    let booted = false;

    const warmUp = (event) => triggerFrom(event) && bootLightbox();
    document.addEventListener('pointerover', warmUp, { passive: true });
    document.addEventListener('focusin', warmUp);

    document.addEventListener('click', (event) => {
        const trigger = triggerFrom(event);

        if (!trigger || booted) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        bootLightbox().then(() => {
            booted = true;
            trigger.click();
        });
    }, { capture: true });
}

// A {{ nocache }} region arrives after the page has loaded and can bring a slider or a
// lightbox trigger with it, so the check runs again once it has been swapped in.
loadSliders();
loadLightbox();
document.addEventListener('statamic:nocache.replaced', loadSliders);

// The map waits for two conditions: the container has to be in view, and the visitor has
// to have accepted the external tiles. On /kaart the map sits above the fold, so it
// still loads straight away; on a location or story page it is below the fold and stays
// out of the measurement window.
function watchMaps() {
    const containers = document.querySelectorAll('[data-map-container]');

    if (!containers.length) {
        return;
    }

    let inView = false;
    let loaded = false;

    const load = () => {
        if (loaded || !inView || !window.getCookieConsent('marketing')) {
            return;
        }

        loaded = true;
        import('./components/_map').then((module) => module.default());
    };

    const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
            inView = true;
            observer.disconnect();
            load();
        }
    }, { rootMargin: '300px' });

    containers.forEach((container) => observer.observe(container));

    window.addEventListener('consent-changed', load);
}

watchMaps();
